import { watch } from 'vue'
import { createRouter, createWebHistory } from 'vue-router'
import store from '../store'
import i18n from '@/i18n'
import { useFeatureFlags, loadFeatureFlags } from '@/composables/useFeatureFlags'
import { useOnboardingGate } from '@/composables/useOnboardingGate'

/**
 * Paths an unapproved supplier may still reach.
 *
 * The registration flow itself, and the settings screen — because two-factor
 * authentication is set up there, and step 8 requires it. Without that exemption the
 * supplier can never satisfy the step that would get them approved, which is a locked
 * door with the key on the other side.
 */
const ONBOARDING_EXEMPT_PATHS = ['/onboarding', '/settings/profile']

// Auth Pages
import LoginPage from '../views/auth/LoginPage.vue'
import RegisterPage from '../views/auth/RegisterPage.vue'
import ForgotPassword from '../views/auth/ForgotPassword.vue'
import ResetPassword from '../views/auth/ResetPassword.vue'
import EmailVerificationPage from '../views/auth/EmailVerificationPage.vue'
import CheckEmailPage from '../views/auth/CheckEmailPage.vue'
import SetPassword from '../views/auth/SetPassword.vue'

// App Pages
import Home from '../views/Home.vue'
import NotFound from '../views/NotFound.vue'
import Profile from '@/views/Profile.vue'
import AdminPanel from '@/views/AdminPanel.vue'
import Databank from '@/views/Databank.vue'
import CertificatesDatabank from '@/views/databank/CertificatesDatabank.vue'
import MailTemplateDatabank from '@/views/databank/MailTemplateDatabank.vue'
import CategoryDatabank from '@/views/databank/CategoryDatabank.vue'
// import SupplierList from '../views/SupplierList.vue' // Removed as the file has been deleted
import RfqList from '../views/RfqList.vue'
import PurchaseOrderList from '../views/PurchaseOrderList.vue'
const PdfTemplateManagement = () => import('../views/super-admin/PdfTemplateManagement.vue')

// User Pages
import UserList from '../views/users/UserList.vue'
import UserForm from '../views/users/UserForm.vue'



// Routes
const routes = [
  // Auth routes
  {
    path: '/login',
    name: 'Login',
    component: LoginPage,
    meta: { requiresAuth: false, titleKey: 'pageTitles.login' }
  },
  {
    path: '/register',
    name: 'Register',
    component: RegisterPage,
    meta: { requiresAuth: false, titleKey: 'pageTitles.register' }
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: ForgotPassword,
    meta: { requiresAuth: false, titleKey: 'pageTitles.forgotPassword' }
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: ResetPassword,
    meta: { requiresAuth: false, titleKey: 'pageTitles.resetPassword' }
  },
  {
    path: '/set-password',
    name: 'SetPassword',
    component: SetPassword,
    meta: { requiresAuth: false, titleKey: 'pageTitles.setPassword' }
  },
  {
    path: '/email-verification',
    name: 'EmailVerification',
    component: EmailVerificationPage,
    meta: { requiresAuth: false, titleKey: 'pageTitles.emailVerification' }
  },
  {
    // Where a new sign-up lands: the account exists but cannot log in until the link in
    // the verification e-mail is opened.
    path: '/check-email',
    name: 'CheckEmail',
    component: CheckEmailPage,
    meta: { requiresAuth: false, titleKey: 'pageTitles.checkEmail' }
  },

  // Supplier registration, steps 3-10. Steps 1 and 2 are /register and
  // /email-verification above; everything from company identity onward needs a signed-in,
  // email-verified user because it writes to a server-side draft.
  // Plan: docs/supplier-registration-plan.md
  {
    path: '/onboarding',
    component: () => import('../views/onboarding/OnboardingShell.vue'),
    meta: { requiresAuth: true, requiresEmailVerified: true, titleKey: 'pageTitles.supplierRegistration' },
    children: [
      // Lands on the first step rather than the first *incomplete* one: the draft is not
      // loaded when this resolves, and the navigator alongside makes the remaining work
      // obvious anyway.
      { path: '', redirect: '/onboarding/company' },
      {
        path: 'company',
        name: 'OnboardingCompany',
        component: () => import('../views/onboarding/CompanyIdentityStep.vue'),
        meta: { titleKey: 'pageTitles.onboardingCompanyIdentity' }
      },
      {
        path: 'addresses',
        name: 'OnboardingAddresses',
        component: () => import('../views/onboarding/AddressesStep.vue'),
        meta: { titleKey: 'pageTitles.onboardingAddresses' }
      },
      {
        path: 'documents',
        name: 'OnboardingDocuments',
        component: () => import('../views/onboarding/DocumentsStep.vue'),
        meta: { titleKey: 'pageTitles.onboardingDocuments' }
      },
      {
        path: 'certificates',
        name: 'OnboardingCertificates',
        component: () => import('../views/onboarding/CertificatesStep.vue'),
        meta: { titleKey: 'pageTitles.onboardingCertificates' }
      },
      {
        path: 'security',
        name: 'OnboardingSecurity',
        component: () => import('../views/onboarding/SecurityStep.vue'),
        meta: { titleKey: 'pageTitles.onboardingSecurity' }
      },
      {
        path: 'review',
        name: 'OnboardingReview',
        component: () => import('../views/onboarding/ReviewStep.vue'),
        meta: { titleKey: 'pageTitles.onboardingReview' }
      }
    ]
  },

  // Application routes
  {
    path: '/',
    name: 'Home',
    component: Home,
    meta: { requiresAuth: true }
  },
  // Dashboard route removed
  {
    // AdminPanel is a shell; each admin screen below is its own child route so
    // the sidebar can link to and highlight it. Navigation is defined once in
    // @/composables/useAdminNav — keep these paths in sync with it.
    path: '/admin',
    component: AdminPanel,
    meta: { requiresAuth: true, requiresSuperAdmin: true, titleKey: 'pageTitles.adminPanel' },
    children: [
      {
        // No redirect here: AdminPanel resolves the landing screen from the user's
        // permissions (see useAdminNav.defaultAdminPath). Hard-coding one would strand
        // an admin who lacks the permission that screen needs.
        path: '',
        name: 'AdminPanel'
      },
      {
        // The platform dashboard is gone: its business figures moved into the analytics
        // screens and its import/integration counters into System Health. The path is
        // kept as a redirect so old links and bookmarks still land somewhere useful.
        path: 'dashboard',
        redirect: { name: 'AdminQuotationAnalytics' }
      },
      // Supplier verification queue (Operations group in useAdminNav).
      {
        path: 'supplier-verification',
        name: 'AdminSupplierVerification',
        component: () => import('../views/super-admin/SupplierVerificationQueue.vue'),
        meta: { requiredPermissions: ['Supplier.SuperAdmin.SupplierVerification'], titleKey: 'pageTitles.supplierVerification' }
      },
      // Analytics group (items 27-34): the landing screen no longer carries the
      // platform statistics; they live here alongside the other analyses.
      {
        path: 'analytics/quotations',
        name: 'AdminQuotationAnalytics',
        component: () => import('../views/super-admin/analytics/QuotationAnalytics.vue'),
        meta: { requiredPermissions: ['Supplier.SuperAdmin.Dashboard'], titleKey: 'pageTitles.quotationAnalytics' }
      },
      {
        path: 'analytics/orders',
        name: 'AdminOrderAnalytics',
        component: () => import('../views/super-admin/analytics/OrderAnalytics.vue'),
        meta: { requiredPermissions: ['Supplier.SuperAdmin.Dashboard'], titleKey: 'pageTitles.orderAnalytics' }
      },
      {
        path: 'analytics/users',
        name: 'AdminUserAnalytics',
        component: () => import('../views/super-admin/analytics/UserAnalytics.vue'),
        meta: { requiredPermissions: ['Supplier.SuperAdmin.Dashboard'], titleKey: 'pageTitles.userAnalytics' }
      },
      {
        path: 'analytics/logins',
        name: 'AdminLoginAnalytics',
        component: () => import('../views/super-admin/analytics/LoginAnalytics.vue'),
        meta: { requiredPermissions: ['Supplier.SuperAdmin.Dashboard'], titleKey: 'pageTitles.loginAnalytics' }
      },
      {
        path: 'analytics/suppliers',
        name: 'AdminSupplierAnalytics',
        component: () => import('../views/super-admin/analytics/SupplierAnalytics.vue'),
        meta: { requiredPermissions: ['Supplier.SuperAdmin.Dashboard'], titleKey: 'pageTitles.supplierAnalysis' }
      },
      {
        path: 'analytics/buyers',
        name: 'AdminBuyerAnalytics',
        component: () => import('../views/super-admin/analytics/BuyerAnalytics.vue'),
        meta: { requiredPermissions: ['Supplier.SuperAdmin.Dashboard'], titleKey: 'pageTitles.buyerAnalysis' }
      },
      {
        // Engineering diagnostics, so it takes the Developer gate like the other tools.
        path: 'analytics/system-health',
        name: 'AdminSystemHealthAnalytics',
        component: () => import('../views/super-admin/analytics/SystemHealthAnalytics.vue'),
        meta: { requiresDeveloperAccess: true, titleKey: 'pageTitles.systemHealth' }
      },
      {
        path: 'tenants',
        name: 'AdminTenants',
        component: () => import('../views/super-admin/TenantManagement.vue'),
        meta: { requiredPermissions: ['Supplier.SuperAdmin.TenantSetup'], titleKey: 'pageTitles.tenants' }
      },
      {
        // Tenant detail lives under the tenants route so the sidebar keeps
        // highlighting "Tenants" while the detail screen is open.
        path: 'tenants/:tenantId',
        name: 'AdminTenantDetail',
        component: () => import('../views/super-admin/TenantDetail.vue'),
        meta: {
          requiredPermissions: ['Supplier.SuperAdmin.TenantSetup'],
          titleKey: 'pageTitles.tenantDetail'
        }
      },
      {
        path: 'users',
        name: 'AdminUsers',
        component: () => import('../views/super-admin/UserManagement.vue'),
        meta: { requiredPermissions: ['Supplier.SuperAdmin.UserManagement'], titleKey: 'pageTitles.users' }
      },
      {
        path: 'setup-wizard',
        name: 'AdminSetupWizard',
        component: () => import('../views/super-admin/SetupWizard.vue'),
        meta: { requiredPermissions: ['Supplier.SuperAdmin.TenantSetup'], titleKey: 'pageTitles.setupWizard' }
      },
      {
        path: 'deleted-tenants',
        name: 'AdminDeletedTenants',
        component: () => import('../views/super-admin/DeletedTenants.vue'),
        meta: { requiredPermissions: ['Supplier.SuperAdmin.TenantSetup'], titleKey: 'pageTitles.deletedTenants' }
      },
      {
        path: 'buyer-api-clients',
        name: 'AdminBuyerApiClients',
        component: () => import('../views/super-admin/BuyerApiClients.vue'),
        meta: { requiredPermissions: ['Supplier.SuperAdmin.TenantSetup'], titleKey: 'pageTitles.buyerApiClients' }
      },
      {
        path: 'supplier-platform-settings',
        name: 'AdminSupplierPlatformSettings',
        component: () => import('../views/super-admin/SupplierPlatformSettingPush.vue'),
        meta: { requiredPermissions: ['Supplier.SuperAdmin.TenantSetup'], titleKey: 'pageTitles.supplierPlatformSettings' }
      },
      {
        path: 'contract-templates',
        name: 'AdminContractTemplates',
        component: () => import('../views/super-admin/ContractTemplates.vue'),
        meta: { requiredPermissions: ['Supplier.SuperAdmin.Contracts'], titleKey: 'pageTitles.contractTemplates' }
      },
      {
        path: 'localization',
        name: 'AdminLocalization',
        component: () => import('../views/super-admin/LocalizationManagement.vue'),
        meta: { requiredPermissions: ['Supplier.SuperAdmin.Localization'], titleKey: 'pageTitles.localization' }
      },
      {
        path: 'user-messages',
        name: 'AdminUserMessages',
        component: () => import('../views/super-admin/UserMessageManagement.vue'),
        meta: { requiredPermissions: ['Supplier.SuperAdmin.UserMessages'], titleKey: 'pageTitles.userMessages' }
      },

      // Developer group — engineering diagnostics, gated separately from the
      // dashboard so non-technical super admins (e.g. a CFO) never see them.
      {
        path: 'import-log-inspector',
        name: 'AdminImportLogInspector',
        component: () => import('../views/super-admin/ImportLogInspector.vue'),
        meta: { requiresDeveloperAccess: true, titleKey: 'pageTitles.importLogInspector' }
      },
      {
        path: 'vesproc-callback-errors',
        name: 'AdminVesprocCallbackErrors',
        component: () => import('../views/super-admin/VesprocCallbackErrors.vue'),
        meta: { requiresDeveloperAccess: true, titleKey: 'pageTitles.callbackErrors' }
      },
      {
        path: 'webhooks',
        name: 'AdminWebhooks',
        component: () => import('../views/super-admin/WebhookManagement.vue'),
        meta: { requiresDeveloperAccess: true, titleKey: 'pageTitles.webhooks' }
      },

      // Subscription group
      {
        path: 'subscription-plans',
        name: 'AdminSubscriptionPlans',
        component: () => import('../views/super-admin/SubscriptionPlans.vue'),
        meta: {
          requiredPermissions: ['Supplier.SuperAdmin.Subscriptions'],
          titleKey: 'pageTitles.subscriptionPlans'
        }
      },
      {
        path: 'tenant-subscriptions',
        name: 'AdminTenantSubscriptions',
        component: () => import('../views/super-admin/TenantSubscriptions.vue'),
        meta: {
          requiredPermissions: ['Supplier.SuperAdmin.Subscriptions'],
          titleKey: 'pageTitles.tenantSubscriptions'
        }
      },
      {
        path: 'discount-codes',
        name: 'AdminDiscountCodes',
        component: () => import('../views/super-admin/DiscountCodes.vue'),
        meta: {
          requiredPermissions: ['Supplier.SuperAdmin.Subscriptions'],
          titleKey: 'pageTitles.discountCodes'
        }
      },
      {
        // Stripe screens expose payment amounts and card metadata across every
        // tenant, so they need the Stripe permission, not Subscriptions.
        path: 'stripe-payments',
        name: 'AdminStripePayments',
        component: () => import('../views/super-admin/StripePayments.vue'),
        meta: {
          requiredPermissions: ['Supplier.SuperAdmin.Stripe'],
          titleKey: 'pageTitles.stripePayments'
        }
      },
      {
        path: 'refund-requests',
        name: 'AdminRefundRequests',
        component: () => import('../views/super-admin/RefundRequests.vue'),
        meta: {
          requiredPermissions: ['Supplier.SuperAdmin.Stripe'],
          titleKey: 'pageTitles.refundRequests'
        }
      },

      {
        path: 'access-management',
        name: 'AdminAccessManagement',
        component: () => import('../views/super-admin/SuperAdminAccessManagement.vue'),
        meta: { requiredPermissions: ['Supplier.SuperAdmin.ManageAccess'], titleKey: 'pageTitles.accessManagement' }
      }
    ]
  },
  {
    path: '/pdf-templates',
    name: 'PdfTemplateManagement',
    component: PdfTemplateManagement,
    meta: { requiresAuth: true, requiresPdfTemplatesAccess: true, titleKey: 'pageTitles.pdfTemplates' }
  },
  {
    path: '/databank',
    name: 'Databank',
    component: Databank,
    meta: { requiresAuth: true, requiresSuperAdmin: true, titleKey: 'pageTitles.databank' }
  },
  {
    path: '/databank/certificates',
    name: 'CertificatesDatabank',
    component: CertificatesDatabank,
    meta: { requiresAuth: true, requiresSuperAdmin: true, titleKey: 'pageTitles.certificates' }
  },
  {
    path: '/databank/mail-template',
    name: 'MailTemplateDatabank',
    component: MailTemplateDatabank,
    meta: { requiresAuth: true, requiresSuperAdmin: true, titleKey: 'pageTitles.mailTemplate' }
  },
  {
    path: '/databank/category',
    name: 'CategoryDatabank',
    component: CategoryDatabank,
    meta: { requiresAuth: true, requiresSuperAdmin: true, titleKey: 'pageTitles.categoryDatabank' }
  },
  {
    path: '/databank/company-types',
    name: 'CompanyTypesDatabank',
    component: () => import('../views/databank/CompanyTypesDatabank.vue'),
    meta: { requiresAuth: true, requiresSuperAdmin: true, titleKey: 'pageTitles.companyTypesDatabank' }
  },
  {
    path: '/databank/user-guide',
    name: 'UserGuideDatabank',
    component: () => import('../views/databank/UserGuideDatabank.vue'),
    meta: { requiresAuth: true, requiresSuperAdmin: true, titleKey: 'pageTitles.userGuide' }
  },
  /* Temporarily removed as SupplierList component has been deleted
  {
    path: '/suppliers',
    name: 'Suppliers',
    component: SupplierList,
    meta: { requiresAuth: true }
  },
  */
  {
    path: '/rfq',
    name: 'RFQ',
    component: RfqList,
    meta: { 
      requiresAuth: true,
      requiredPermissions: ['Supplier.Quotations.ViewDetails', 'Supplier.Quotations.Create', 'Supplier.Quotations.Edit', 'Supplier.Quotations.Approve', 'Supplier.Quotations'],
      titleKey: 'pageTitles.rfq'
    }
  },
  {
    path: '/rfq-list',
    name: 'RfqList',
    component: () => import('../views/RfqList.vue'),
    meta: { 
      requiresAuth: true,
      requiredPermissions: ['Supplier.Quotations.ViewDetails', 'Supplier.Quotations'],
      titleKey: 'pageTitles.rfqList'
    }
  },
  {
    path: '/rfq/:id',
    name: 'RfqEdit',
    component: () => import('../views/RfqEdit.vue'),
    meta: { 
      requiresAuth: true,
      requiredPermissions: ['Supplier.Quotations.ViewDetails', 'Supplier.Quotations.Edit', 'Supplier.Quotations'],
      titleKey: 'pageTitles.editRfq'
    }
  },
  {
    path: '/rfq/:id/reject',
    name: 'RfqReject',
    component: () => import('../views/RfqEdit.vue'),
    meta: { 
      requiresAuth: true,
      requiredPermissions: ['Supplier.Quotations.ViewDetails', 'Supplier.Quotations.Edit', 'Supplier.Quotations'],
      titleKey: 'pageTitles.declineRfq'
    }
  },
  {
    path: '/purchase-order',
    name: 'PurchaseOrder',
    component: PurchaseOrderList,
    meta: { 
      requiresAuth: true,
      requiredPermissions: ['Supplier.PurchaseOrders.ViewDetails', 'Supplier.PurchaseOrders.Create', 'Supplier.PurchaseOrders.Edit', 'Supplier.PurchaseOrders.Approve', 'Supplier.PurchaseOrders'],
      titleKey: 'pageTitles.salesOrder'
    }
  },
  {
    path: '/purchase-order-list',
    name: 'PurchaseOrderList',
    component: () => import('../views/PurchaseOrderList.vue'),
    meta: { 
      requiresAuth: true,
      requiredPermissions: ['Supplier.PurchaseOrders.ViewDetails', 'Supplier.PurchaseOrders'],
      titleKey: 'pageTitles.purchaseOrderList'
    }
  },
  {
    path: '/purchase-order/:id',
    name: 'PurchaseOrderEdit',
    component: () => import('../views/PurchaseOrderEdit.vue'),
    meta: { 
      requiresAuth: true,
      requiredPermissions: ['Supplier.PurchaseOrders.ViewDetails', 'Supplier.PurchaseOrders.Edit', 'Supplier.PurchaseOrders'],
      titleKey: 'pageTitles.purchaseOrderDetails'
    }
  },
  {
    path: '/profile',
    name: 'Profile',
    component: Profile,
    meta: { requiresAuth: true, requiresNonLookupAccess: true, titleKey: 'pageTitles.profile' }
  },
  // Profile sections reachable on their own from the sidebar (item 35). Declared before
  // the /profile/:profileMode catch-all so these literal paths win.
  {
    path: '/profile/certificates',
    name: 'ProfileCertificates',
    component: () => import('../views/profile/ProfileCertificates.vue'),
    meta: { requiresAuth: true, requiresNonLookupAccess: true, titleKey: 'pageTitles.certificates' }
  },
  {
    path: '/profile/reviews',
    name: 'ProfileReviews',
    component: () => import('../views/profile/ProfileReviews.vue'),
    meta: { requiresAuth: true, requiresNonLookupAccess: true, titleKey: 'pageTitles.reviews' }
  },
  {
    path: '/profile/ports',
    name: 'ProfilePorts',
    component: () => import('../views/profile/ProfilePorts.vue'),
    meta: { requiresAuth: true, requiresNonLookupAccess: true, titleKey: 'pageTitles.ports' }
  },
  {
    path: '/profile/categories',
    name: 'ProfileCategories',
    component: () => import('../views/profile/ProfileCategories.vue'),
    meta: { requiresAuth: true, requiresNonLookupAccess: true, titleKey: 'pageTitles.categories' }
  },
  {
    path: '/profile/:profileMode',
    name: 'ProfileByMode',
    component: Profile,
    meta: { requiresAuth: true, requiresLookupAccess: true, titleKey: 'pageTitles.profile' }
  },
  {
    path: '/lookup',
    name: 'LookUp',
    component: () => import('../views/LookUp.vue'),
    meta: { requiresAuth: true, requiresLookupAccess: true, titleKey: 'pageTitles.lookup' }
  },
  {
    path: '/quotation-mail-recipients',
    name: 'QuotationMailRecipients',
    component: () => import('../views/quotation-mail-recipients/QuotationMailRecipients.vue'),
    meta: { requiresAuth: true, requiresLookupAccess: true, titleKey: 'pageTitles.quotationMailRecipients' }
  },
  {
    path: '/tenant-review',
    name: 'TenantReview',
    component: () => import('../views/tenant-review/TenantReview.vue'),
    meta: { requiresAuth: true, requiresLookupAccess: true, titleKey: 'pageTitles.supplierFinder' }
  },
  
  // Mail Tracking routes (for buyer tenants)
  {
    path: '/mail-tracking',
    name: 'MailTrackingDashboard',
    component: () => import('../views/mail-tracking/MailTrackingDashboard.vue'),
    meta: { requiresAuth: true, requiresLookupAccess: true, titleKey: 'pageTitles.mailTracking' }
  },
  {
    path: '/mail-tracking/list',
    name: 'MailTrackingList',
    component: () => import('../views/mail-tracking/MailTrackingList.vue'),
    meta: { requiresAuth: true, requiresLookupAccess: true, titleKey: 'pageTitles.mailTrackingList' }
  },
  {
    path: '/mail-tracking/details/:id',
    name: 'MailTrackingDetails',
    component: () => import('../views/mail-tracking/MailTrackingDetails.vue'),
    meta: { requiresAuth: true, requiresLookupAccess: true, titleKey: 'pageTitles.mailTrackingDetails' }
  },
  {
    // The settings landing page was a menu of two links the sidebar already lists, so
    // it is gone. Kept as a redirect: bookmarks and old links land on the first
    // settings screen instead of a 404.
    path: '/settings',
    redirect: '/settings/profile'
  },
  {
    path: '/subscription/plans',
    name: 'SubscriptionPlansPublic',
    component: () => import('../views/SubscriptionPlansPublic.vue'),
    meta: { requiresAuth: true, requiresNonLookupAccess: true, requiresSubscriptionFeature: true, titleKey: 'pageTitles.subscriptionPlans' }
  },
  {
    // Where a tenant manages the subscription it already has: plan, cancel and
    // resume, refund request, and its invoices. Kept apart from /subscription/plans,
    // which is the catalogue you browse before buying.
    path: '/subscription/mine',
    name: 'MySubscription',
    component: () => import('../views/MySubscription.vue'),
    meta: { requiresAuth: true, requiresNonLookupAccess: true, requiresSubscriptionFeature: true, titleKey: 'pageTitles.mySubscription' }
  },
  {
    // A tenant's own accepted contracts; no extra permission, the API scopes it.
    path: '/contracts',
    name: 'MyContracts',
    component: () => import('../views/MyContracts.vue'),
    meta: { requiresAuth: true, requiresNonLookupAccess: true, requiresContractsFeature: true, titleKey: 'pageTitles.myContracts' }
  },
  {
    // Culture moved into the settings screen. Kept as a redirect so existing bookmarks
    // any links still pointing here land on the settings rather than a 404.
    path: '/culture',
    redirect: '/settings/profile'
  },
  {
    path: '/settings/profile',
    name: 'ProfileSettings',
    component: () => import('../views/ProfileSettings.vue'),
    meta: { requiresAuth: true, titleKey: 'pageTitles.settings' }
  },
  {
    path: '/settings/quotation-expiry',
    name: 'QuotationExpirySettings',
    component: () => import('../views/QuotationExpirySettings.vue'),
    meta: { requiresAuth: true, requiresLookupAccess: true, titleKey: 'pageTitles.quotationExpirySettings' }
  },
  {
    path: '/help',
    name: 'Help',
    component: () => import('../views/Help.vue'),
    meta: { requiresAuth: true, titleKey: 'pageTitles.help' }
  },
  {
    path: '/notifications',
    name: 'Notifications',
    component: () => import('../views/NotificationList.vue'),
    meta: { requiresAuth: true, titleKey: 'pageTitles.notifications' }
  },
  
  // User Management routes
  {
    path: '/users',
    name: 'UserList',
    component: UserList,
    meta: { 
      requiresAuth: true,
      requiredPermissions: ['Supplier.Users', 'Supplier.Administration'],
      titleKey: 'pageTitles.users'
    }
  },
  {
    path: '/users/create',
    name: 'CreateUser',
    component: UserForm,
    meta: { 
      requiresAuth: true,
      requiredPermissions: ['Supplier.Users', 'Supplier.Administration'],
      titleKey: 'pageTitles.createUser'
    }
  },
  {
    path: '/users/edit/:id',
    name: 'EditUser',
    component: UserForm,
    meta: { 
      requiresAuth: true,
      requiredPermissions: ['Supplier.Users', 'Supplier.Administration'],
      titleKey: 'pageTitles.editUser'
    }
  },
  

  
  // Catch all (404)
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: NotFound,
    meta: { titleKey: 'pageTitles.notFound' }
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    // Always scroll to top
    return { top: 0 };
  }
})

// Dynamic title functionality.
// meta.titleKey is an i18n key; it is translated when the title is set (never at module
// load, the texts arrive asynchronously) and again whenever the UI language changes.
const applyDocumentTitle = route => {
  const titleKey = route?.meta?.titleKey
  document.title = titleKey ? `VESPROC - ${i18n.global.t(titleKey)}` : 'VESPROC'
}

router.afterEach(to => applyDocumentTitle(to))

watch(
  () => i18n.global.locale.value,
  () => applyDocumentTitle(router.currentRoute.value)
)

// Navigation Guards
router.beforeEach(async (to, from, next) => {
  const isAuthenticated = store.getters['auth/isAuthenticated']
  const currentUser = store.getters['auth/currentUser']
  const hasAnyPermission = store.getters['auth/hasAnyPermission']
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth)
  const requiresLookupAccess = to.matched.some(record => record.meta.requiresLookupAccess)
  const requiresNonLookupAccess = to.matched.some(record => record.meta.requiresNonLookupAccess)
  const requiresSuperAdmin = to.matched.some(record => record.meta.requiresSuperAdmin)
  const requiresPdfTemplatesAccess = to.matched.some(record => record.meta.requiresPdfTemplatesAccess)
  const requiresSubscriptionFeature = to.matched.some(record => record.meta.requiresSubscriptionFeature)
  const requiresContractsFeature = to.matched.some(record => record.meta.requiresContractsFeature)
  const requiresDeveloperAccess = to.matched.some(record => record.meta.requiresDeveloperAccess)
  const requiresEmailVerified = to.matched.some(record => record.meta.requiresEmailVerified)
  if (requiresSubscriptionFeature || requiresContractsFeature) await loadFeatureFlags()
  const { features } = useFeatureFlags()

  // Mirrors useDeveloperPermissions, which the backend enforces on the matching
  // endpoints. Keep both in sync.
  const hasDeveloperAccess = hasAnyPermission(['Supplier.SuperAdmin.Developer'])
  
  // Get required permissions for the route
  const requiredPermissions = to.matched
    .filter(record => record.meta.requiredPermissions)
    .map(record => record.meta.requiredPermissions)
    .flat()
  
  // Check if user has buyer tenant access
  const hasBuyerTenantAccess = currentUser?.editionId?.toUpperCase() === '98CAF202-9D8E-B62F-01C1-39FF3E9F71A0'

  // Special edition access to PDF Template Management
  const allowedPdfTemplatesEditionId = 'B0CB311A-A4D0-65B4-DF1B-39FF3E9F716E'
  const editionMatchesPdfTemplatesAccess = currentUser?.editionId?.toUpperCase() === allowedPdfTemplatesEditionId
  const printTemplatePermissions = [
    'Supplier.PrintTemplates',
    'Supplier.PrintTemplates.Create',
    'Supplier.PrintTemplates.Edit',
    'Supplier.PrintTemplates.Delete'
  ]
  const hasPrintTemplatesPermission = hasAnyPermission(printTemplatePermissions)
  const hasDatabankPermission = hasAnyPermission(['Supplier.SuperAdmin.Databank'])
  const basePdfTemplatesTenantAccess = currentUser?.isImpersonating
    ? editionMatchesPdfTemplatesAccess
    : Boolean(currentUser?.isSuperAdmin || editionMatchesPdfTemplatesAccess)
  // Supplier.SuperAdmin.Databank bypasses the PrintTemplates permission check.
  const hasPdfTemplatesAccess = basePdfTemplatesTenantAccess && (hasDatabankPermission || Boolean(currentUser?.isSuperAdmin) || hasPrintTemplatesPermission)
  
  // If route requires auth and user is not authenticated
  if (requiresAuth && !isAuthenticated) {
    // Save the original URL to redirect after login
    next({
      path: '/login',
      query: { redirect: to.fullPath }
    })
  } 
  // Onboarding steps 3-10 are gated behind step 2.
  //
  // Deliberately fails open: the login payload does not carry emailConfirmed today
  // (see authService._createUserObject), so treating "unknown" as unverified would shut
  // every supplier out of their own registration. Only an explicit false redirects.
  // Phase 2 adds the flag to the payload, at which point this becomes a real gate — the
  // backend enforces it on the onboarding endpoints regardless.
  else if (requiresEmailVerified && currentUser?.emailConfirmed === false) {
    next('/email-verification')
  }
  // If route requires super admin access and user is not super admin
  else if (requiresSuperAdmin && !currentUser?.isSuperAdmin) {
    
    next('/profile') // Redirect to profile if not super admin
  }
  // If user is impersonating and trying to access admin panel, redirect appropriately
  else if (requiresSuperAdmin && currentUser?.isImpersonating) {
    // Check if impersonated user is a buyer tenant
    if (hasBuyerTenantAccess) {
      window.location.href = '/lookup'; // Redirect buyer tenants to lookup page
    } else {
      window.location.href = '/profile'; // Redirect normal users to profile page
    }
    return;
  }
  // If route requires special PDF templates access and user doesn't have it
  else if (requiresPdfTemplatesAccess && !hasPdfTemplatesAccess) {
    next('/settings/profile')
  }
  // If route requires the subscription feature and it's disabled
  else if (requiresSubscriptionFeature && !features.subscription) {
    next('/profile')
  }
  // If route requires the contracts feature and it's disabled
  else if (requiresContractsFeature && !features.contracts) {
    next('/profile')
  }
  // Developer tools (import logs, callback errors, webhooks)
  else if (requiresDeveloperAccess && !hasDeveloperAccess) {
    next('/profile')
  }
  // If route requires specific permissions and user doesn't have any of them
  else if (requiredPermissions.length > 0 && !hasAnyPermission(requiredPermissions)) {
    
    next('/profile') // Redirect to profile if missing permissions
  }
  // If route requires buyer tenant access and user doesn't have it (super admin bypasses)
  else if (requiresLookupAccess && !hasBuyerTenantAccess && !currentUser?.isSuperAdmin) {
    next('/profile') // Redirect to profile if no buyer tenant access
  }
  // If route requires non-buyer tenant access and user is a buyer tenant
  else if (requiresNonLookupAccess && hasBuyerTenantAccess) {
    next('/lookup') // Redirect buyer tenants to their main page
  }
  // If user is authenticated and trying to access auth page (except reset-password which can be accessed with valid token)
  else if (isAuthenticated && (to.path === '/login' || to.path === '/register' || to.path === '/forgot-password')) {
    next('/profile')
  } 
  // An account that has not been approved sees the registration flow and nothing else.
  //
  // Enforced here rather than by hiding menu items: a hidden link is still a reachable
  // URL. Super admins and buyer tenants are exempt inside isConfinedToOnboarding.
  else if (isAuthenticated && !ONBOARDING_EXEMPT_PATHS.some(path => to.path.startsWith(path))) {
    const { isConfinedToOnboarding } = useOnboardingGate()
    if (await isConfinedToOnboarding()) {
      next('/onboarding')
    } else {
      next()
    }
  }
  // Otherwise proceed normally
  else {
    next()
  }
})

export default router 